import React, { useState } from 'react';

import { Button, Group, Modal, Radio, Stack, Text, Title } from '@mantine/core';

export type RecurrenceChoice = 'this-event' | 'all-future-events';

interface IModalDragRecurrenceConfirmationProps {
  opened: boolean;
  eventName: string;
  onCancel: () => void;
  onContinue: (choice: RecurrenceChoice) => void;
}

const ModalDragRecurrenceConfirmation = ({
  opened,
  eventName,
  onCancel,
  onContinue,
}: IModalDragRecurrenceConfirmationProps): JSX.Element => {
  const [choice, setChoice] = useState<RecurrenceChoice>('this-event');

  const handleCancel = (): void => {
    setChoice('this-event');
    onCancel();
  };

  const handleContinue = (): void => {
    onContinue(choice);
    setChoice('this-event');
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleCancel}
      title={
        <Title order={3} fw={700}>
          Edit {eventName}
        </Title>
      }
      size={460}
      styles={{
        header: { padding: '2.5rem 2rem 0.75rem', position: 'relative' },
        title: { width: '100%', textAlign: 'center' },
        close: {
          position: 'absolute',
          top: '1.25rem',
          right: '1.25rem',
          '& svg': { width: 20, height: 20, strokeWidth: 2, color: 'black' },
        },
        body: { padding: '1.5rem 3rem 3.5rem' },
      }}
    >
      <Stack align='center' spacing={44}>
        <Text align='center' size='sm' sx={{ whiteSpace: 'nowrap' }}>
          Do you want to edit this occurrence or all future events?
        </Text>
        <Radio.Group value={choice} onChange={value => setChoice(value as RecurrenceChoice)}>
          <Stack spacing='md'>
            <Radio
              value='this-event'
              label='This event'
              color='kiloBlue'
              size='md'
              styles={{ label: { fontWeight: 700 } }}
            />
            <Stack spacing={4}>
              <Radio
                value='all-future-events'
                label='All future events'
                color='kiloBlue'
                size='md'
                styles={{ label: { fontWeight: 700 } }}
              />
              <Text size='xs' color='dimmed' pl={34}>
                Only the time and duration can be edited for events in a series. To move this series to a
                different day, set an end date on this series and create a new recurring event on the desired
                day.
              </Text>
            </Stack>
          </Stack>
        </Radio.Group>
        <Group position='center'>
          <Button variant='outline' onClick={handleCancel}>
            Cancel
          </Button>
          <Button color='kiloBlue' onClick={handleContinue}>
            Continue
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalDragRecurrenceConfirmation;
